/**
 * Care Calendar Overlay Component
 */

import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { ScrollArea } from "../ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Calendar, Plus, Clock, MapPin } from "lucide-react";
import { toast } from "sonner";

export function CareCalendarOverlay({ patientId, patientName, onSave, onClose }) {
  const [events, setEvents] = useState([
    { id: "1", title: "RN Routine Visit", event_type: "visit", discipline: "RN", start_time: new Date().toISOString(), location: "Patient Home" },
    { id: "2", title: "IDG Meeting", event_type: "idg", start_time: new Date().toISOString(), location: "Office" },
    { id: "3", title: "Social Worker Assessment", event_type: "visit", discipline: "MSW", start_time: new Date().toISOString(), location: "Patient Home" },
    { id: "4", title: "Chaplain Visit", event_type: "visit", discipline: "Chaplain", start_time: new Date().toISOString(), location: "Patient Home" },
    { id: "5", title: "DME Equipment Delivery", event_type: "delivery", start_time: new Date().toISOString(), location: "Patient Home" },
  ]);
  const [showAdd, setShowAdd] = useState(false);
  const [newEvent, setNewEvent] = useState({
    title: "",
    event_type: "visit",
    start_time: "",
    end_time: "",
    location: "",
    notes: "",
    discipline: ""
  });

  const eventTypes = [
    { value: "visit", label: "Home Visit", color: "bg-green-500" },
    { value: "appointment", label: "Appointment", color: "bg-blue-500" },
    { value: "idg", label: "IDG Meeting", color: "bg-purple-500" },
    { value: "call", label: "Phone Call", color: "bg-amber-500" },
    { value: "delivery", label: "DME Delivery", color: "bg-teal-500" },
    { value: "recert", label: "Recertification", color: "bg-red-500" },
  ];

  const disciplines = ["RN", "LVN", "MSW", "Chaplain", "HHA", "PT", "OT", "SLP", "MD"];

  const getEventColor = (type) => eventTypes.find(t => t.value === type)?.color || "bg-slate-500";

  const formatEventTime = (isoString) => {
    if (!isoString) return "";
    return new Date(isoString).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const formatEventDate = (isoString) => {
    if (!isoString) return "";
    return new Date(isoString).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const handleAddEvent = () => {
    if (!newEvent.title || !newEvent.start_time) {
      toast.error("Please fill in event title and start time");
      return;
    }
    const event = { ...newEvent, id: Date.now().toString() };
    setEvents(prev => [...prev, event]);
    setShowAdd(false);
    setNewEvent({ title: "", event_type: "visit", start_time: "", end_time: "", location: "", notes: "", discipline: "" });
    toast.success("Event added to calendar");
  };

  const upcomingEvents = events
    .filter(e => new Date(e.start_time) >= new Date())
    .sort((a, b) => new Date(a.start_time) - new Date(b.start_time))
    .slice(0, 10);

  return (
    <div className="space-y-4 p-4" data-testid="care-calendar-overlay">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-blue-400" />
          <h3 className="font-semibold text-white">Care Calendar</h3>
          <Badge variant="outline" className="border-slate-600 text-slate-300">{patientName}</Badge>
        </div>
        <Button size="sm" onClick={() => setShowAdd(true)} className="bg-blue-600 hover:bg-blue-700">
          <Plus className="w-4 h-4 mr-1" />Add Event
        </Button>
      </div>

      {/* Event Type Legend */}
      <div className="flex flex-wrap gap-2 p-3 bg-slate-800/50 rounded-lg border border-slate-700">
        {eventTypes.map(type => (
          <div key={type.value} className="flex items-center gap-1">
            <div className={`w-2 h-2 rounded-full ${type.color}`} />
            <span className="text-xs text-slate-400">{type.label}</span>
          </div>
        ))}
      </div>

      {/* Upcoming Events */}
      <div className="space-y-2">
        <Label className="text-slate-400">Upcoming Schedule</Label>
        <ScrollArea className="h-[400px] pr-2">
          <div className="space-y-2">
            {upcomingEvents.length > 0 ? upcomingEvents.map(event => (
              <div key={event.id} className="p-3 rounded-lg bg-slate-800/50 border border-slate-700 hover:border-slate-600">
                <div className="flex items-start gap-3">
                  <div className={`w-1 h-full min-h-[50px] rounded-full ${getEventColor(event.event_type)}`} />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className={`${getEventColor(event.event_type)} text-white text-xs`}>
                        {eventTypes.find(t => t.value === event.event_type)?.label}
                      </Badge>
                      {event.discipline && <Badge variant="outline" className="text-xs border-slate-600 text-slate-300">{event.discipline}</Badge>}
                    </div>
                    <p className="font-medium text-sm text-white">{event.title}</p>
                    <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />{formatEventDate(event.start_time)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />{formatEventTime(event.start_time)}
                      </span>
                      {event.location && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />{event.location}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )) : (
              <div className="text-center py-8 text-slate-400">
                <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No upcoming events</p>
              </div>
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Add Event Dialog */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowAdd(false)}>
          <div className="bg-slate-900 border border-slate-700 rounded-lg p-6 w-[450px]" onClick={e => e.stopPropagation()}>
            <h4 className="font-semibold mb-4 flex items-center gap-2 text-white"><Calendar className="w-4 h-4" />Add Event</h4>
            <div className="space-y-3">
              <div className="space-y-1">
                <Label className="text-xs text-slate-300">Event Title *</Label>
                <Input value={newEvent.title} onChange={e => setNewEvent({...newEvent, title: e.target.value})} placeholder="e.g., RN Visit" className="bg-slate-800 border-slate-700 text-white" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-slate-300">Event Type</Label>
                  <Select value={newEvent.event_type} onValueChange={v => setNewEvent({...newEvent, event_type: v})}>
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white"><SelectValue /></SelectTrigger>
                    <SelectContent>{eventTypes.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-300">Discipline</Label>
                  <Select value={newEvent.discipline} onValueChange={v => setNewEvent({...newEvent, discipline: v})}>
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>{disciplines.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-slate-300">Start Time *</Label>
                  <Input type="datetime-local" value={newEvent.start_time} onChange={e => setNewEvent({...newEvent, start_time: e.target.value})} className="bg-slate-800 border-slate-700 text-white" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-300">End Time</Label>
                  <Input type="datetime-local" value={newEvent.end_time} onChange={e => setNewEvent({...newEvent, end_time: e.target.value})} className="bg-slate-800 border-slate-700 text-white" />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-300">Location</Label>
                <Input value={newEvent.location} onChange={e => setNewEvent({...newEvent, location: e.target.value})} placeholder="e.g., Patient Home" className="bg-slate-800 border-slate-700 text-white" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-300">Notes</Label>
                <Textarea value={newEvent.notes} onChange={e => setNewEvent({...newEvent, notes: e.target.value})} rows={2} className="bg-slate-800 border-slate-700 text-white" />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowAdd(false)} className="border-slate-600 text-slate-300">Cancel</Button>
                <Button onClick={handleAddEvent} className="bg-blue-600 hover:bg-blue-700"><Plus className="w-4 h-4 mr-1" />Add Event</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-end gap-2 pt-4">
        <Button variant="outline" onClick={onClose} className="border-slate-600 text-slate-300 hover:bg-slate-800">Close</Button>
      </div>
    </div>
  );
}

export default CareCalendarOverlay;
